import { memo, useMemo } from "react";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useSessionContext } from "@/contexts/SessionContext";
import {
  ClinicalCrmSectionsBoard,
  CrmSetupStepperSection,
  useClinicalCrmManagementData,
} from "src-next/modules/crm-clinical";
import { resolveCrmOrganizationId } from "src-next/modules/crm-clinical/communications/helpers/resolveCrmOrganizationId";
import { useClinicalCrmSectionsBoardModel } from "src-next/modules/crm-clinical/management/hooks";
import { LargeContentBox } from "@/components/common";

type ClinicalCrmManagementPageProps = {
  /**
   * Contexto do tenant (organização) injetado pela aplicação/roteador.
   * Quando omitido, a página mantém o fallback atual definido no hook.
   */
  organizationId?: string;
  /**
   * Contexto do usuário/profissional injetado pela aplicação/roteador.
   * Quando omitido, a página mantém o fallback atual definido no hook.
   */
  practitionerId?: string;
  /**
   * Oculta fluxos clínicos no FluxZap (ex.: organização ecommerce):
   * automações, pós-atendimento, questionários e modelos padrão.
   * Somente UI — backend/FHIR intactos.
   */
  hideAutomationsSection?: boolean;
};

const ClinicalCrmManagementPage = memo(
  (props: ClinicalCrmManagementPageProps) => {
    const { access } = useSessionContext();
    const resolvedOrganizationId = useMemo(
      () =>
        resolveCrmOrganizationId(props.organizationId, access?.organizationId),
      [props.organizationId, access?.organizationId],
    );

    const {
      activeTabId,
      setActiveTabId,
      messageTemplates,
      isSetupLoading,
      shouldShowStepper,
      currentOnboardingStepId,
      isCrmConfigurationAccountsConfigured,
      isCrmConfigurationLoading,
      organizationId,
      practitionerId,
      handleGoToStep,
      handleCompleteOnboarding,
      handleCreateTemplate,
      handleUpdateTemplate,
      handleDeleteTemplate,
      handleRefreshMessageTemplates,
      isMessageTemplatesLoading,
      crmWhatsappWabaId,
      crmWhatsappChannelOption,
      crmSetupConfiguration,
      hasActiveMetaEndpoint,
      isEndpointStatusLoading,
      hasFluxZapExtensionActive,
      isFluxZapExtensionLoading,
      refetchFluxZapExtensionStatus,
    } = useClinicalCrmManagementData({
      organizationId: resolvedOrganizationId,
      practitionerId: props.practitionerId,
    });

    const sectionsContentModel = useMemo(
      () => ({
        organizationId,
        practitionerId,
        wabaId: crmWhatsappWabaId,
        whatsappChannelOption: crmWhatsappChannelOption,
        crmSetupConfiguration,
        activeTabId,
        setActiveTabId,
        messageTemplates,
        handleCreateTemplate,
        handleUpdateTemplate,
        handleDeleteTemplate,
        handleRefreshMessageTemplates,
        isMessageTemplatesLoading,
        hasActiveMetaEndpoint,
        isEndpointStatusLoading,
        hasFluxZapExtensionActive,
        isFluxZapExtensionLoading,
        refetchFluxZapExtensionStatus,
      }),
      [
        organizationId,
        practitionerId,
        crmWhatsappWabaId,
        crmWhatsappChannelOption,
        crmSetupConfiguration,
        activeTabId,
        setActiveTabId,
        messageTemplates,
        handleCreateTemplate,
        handleUpdateTemplate,
        handleDeleteTemplate,
        handleRefreshMessageTemplates,
        isMessageTemplatesLoading,
        hasActiveMetaEndpoint,
        isEndpointStatusLoading,
        hasFluxZapExtensionActive,
        isFluxZapExtensionLoading,
        refetchFluxZapExtensionStatus,
      ],
    );
    const sectionsBoardModel =
      useClinicalCrmSectionsBoardModel(sectionsContentModel);

    if (isSetupLoading) {
      return (
        <LargeContentBox>
          <Box sx={{ p: { xs: 2, sm: 3 }, pb: 1.5 }}>
            <Typography variant="h6" fontWeight={700}>
              FluxZap
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Carregando configuração inicial...
            </Typography>
          </Box>
          <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
            <LinearProgress sx={{ borderRadius: 999 }} />
          </Box>
        </LargeContentBox>
      );
    }

    return (
      <LargeContentBox>
        <Stack spacing={3} sx={{ py: 0.5, pb: 4, width: "100%" }}>
          {shouldShowStepper ? (
            <CrmSetupStepperSection
              practitionerId={practitionerId}
              organizationId={organizationId}
              currentStepId={currentOnboardingStepId}
              onGoToStep={handleGoToStep}
              onComplete={handleCompleteOnboarding}
              setupConfiguration={crmSetupConfiguration}
              isCrmConfigurationAccountsConfigured={
                isCrmConfigurationAccountsConfigured
              }
              isCrmConfigurationLoading={isCrmConfigurationLoading}
              onBeforeNext={async (stepId) => {
                if (stepId !== "configuration") return;
                if (
                  crmSetupConfiguration.configurationStatus?.selectedOption !==
                  "metaEmbeddedSignup"
                ) {
                  return;
                }
                const metaConnection =
                  crmSetupConfiguration.configurationStatus?.metaConnection;
                if (!metaConnection?.wabaId) return;

                await crmSetupConfiguration.saveMetaAccountSelection();
              }}
              isBeforeNextLoading={
                crmSetupConfiguration.isSavingMetaAccountSelection
              }
            />
          ) : (
            <>
              <ClinicalCrmSectionsBoard
                model={sectionsBoardModel}
                hideAutomationsSection={props.hideAutomationsSection}
              />
            </>
          )}
        </Stack>
      </LargeContentBox>
    );
  },
);

ClinicalCrmManagementPage.displayName = "ClinicalCrmManagementPage";

export default ClinicalCrmManagementPage;
